import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card, CardContent, Typography, TextField, Button, Alert, Stack, Switch, FormControlLabel,
  Checkbox, CircularProgress, Chip, Divider, Link,
} from '@mui/material';
import { Sms, Email, CheckCircle } from '@mui/icons-material';
import Layout from '../components/Layout';
import { apiService, errorMessage } from '../services/api';
import { UserProfile } from '../types';

const NotificationSettings: React.FC = () => {
  const [me, setMe] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiService.getMe()
      .then((u) => {
        setMe(u);
        setName(u.name || '');
        if (u.phone && !u.phoneVerified) { setPhone(u.phone); setStep('code'); }
      })
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  const run = async (fn: () => Promise<UserProfile | void>, success?: string) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const result = await fn();
      if (result) setMe(result);
      if (success) setInfo(success);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const saveName = () => run(() => apiService.updateMe({ name }), 'Name saved');

  const sendCode = () => run(async () => {
    if (!consent) throw new Error('Please tick the box to agree to receive texts');
    await apiService.setPhone(phone, true);
    setStep('code');
  }, 'We texted you a 6-digit code');

  const verify = () => run(async () => {
    const u = await apiService.verifyPhone(code);
    setStep('phone');
    setCode('');
    return u;
  }, 'Phone verified. Text reminders are on.');

  const toggleSms = (optIn: boolean) => run(() => apiService.setSmsOptIn(optIn), optIn ? 'Text reminders on' : 'Text reminders off');
  const removePhone = () => run(async () => {
    const u = await apiService.removePhone();
    setPhone('');
    setStep('phone');
    return u;
  }, 'Phone number removed');

  return (
    <Layout title="Notification settings" backTo="/dashboard" maxWidth="sm" loading={loading}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      {me && (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Your name</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Shown next to gift ideas you suggest (e.g. "Mom suggested").
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
                <Button variant="contained" onClick={saveName} disabled={busy}>Save</Button>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Email color="primary" /> Email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Match announcements and reminders go to <strong>{me.email}</strong>. Email is always on.
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Sms color="secondary" /> Text messages
              </Typography>

              {!me.smsAvailable ? (
                <Alert severity="info">Text reminders aren't available yet. Email reminders still work.</Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Get a text when your match is ready and before the exchange. Texts only ever contain a
                    link to the app, never your person's name, so a glance at your lock screen gives nothing away.
                  </Typography>

                  {me.phoneVerified && me.phone ? (
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip icon={<CheckCircle />} color="success" label={me.phone} />
                        <Button size="small" color="error" onClick={removePhone} disabled={busy}>Remove</Button>
                      </Stack>
                      <FormControlLabel
                        control={<Switch checked={me.smsOptIn} onChange={(e) => toggleSms(e.target.checked)} disabled={busy} />}
                        label={me.smsOptIn ? 'Text reminders are on' : 'Text reminders are off'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Reply STOP to any text to opt out, or turn this off here. Message and data rates may apply.
                      </Typography>
                    </Stack>
                  ) : step === 'phone' ? (
                    <Stack spacing={2}>
                      <TextField
                        label="Mobile number" value={phone} onChange={(e) => setPhone(e.target.value)}
                        placeholder="(555) 555-0100" inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
                        label={
                          <Typography variant="body2">
                            I agree to receive Secret Santa text messages at this number (a match-ready notice and a
                            few reminders per season). Message and data rates may apply. Reply STOP to cancel, HELP for help.
                            See the <Link component={RouterLink} to="/terms" onClick={(e) => e.stopPropagation()}>terms</Link> and{' '}
                            <Link component={RouterLink} to="/privacy" onClick={(e) => e.stopPropagation()}>privacy policy</Link>.
                          </Typography>
                        }
                      />
                      <Button variant="contained" onClick={sendCode} disabled={busy || !phone || !consent}>
                        {busy ? <CircularProgress size={22} /> : 'Text me a verification code'}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Typography variant="body2">Enter the code we sent to <strong>{me.phone || phone}</strong>.</Typography>
                      <TextField
                        label="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={verify} disabled={busy || code.length < 4}>
                          {busy ? <CircularProgress size={22} /> : 'Verify'}
                        </Button>
                        <Button onClick={() => setStep('phone')} disabled={busy}>Change number</Button>
                        <Button onClick={sendCode} disabled={busy}>Resend code</Button>
                      </Stack>
                    </Stack>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Your number is only used for these reminders and is never shared with other participants.
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}
    </Layout>
  );
};

export default NotificationSettings;
